import { useEffect, useReducer, useCallback, useMemo } from 'react';
// utils
import axios from 'src/utils/axios';
import { HOST_API } from 'src/config-global';
//
import { AuthContext } from './auth-context';
import { isValidToken, setSession } from './utils';
import { ActionMapType, AuthStateType, AuthUserType } from '../../types';

// ----------------------------------------------------------------------

// NOTE:
// We only build demo at basic level.
// Customer will need to do some extra handling yourself if you want to extend the logic and other features...

// ----------------------------------------------------------------------

enum Types {
  INITIAL = 'INITIAL',
  LOGIN = 'LOGIN',
  REGISTER = 'REGISTER',
  LOGOUT = 'LOGOUT',
}

type Payload = {
  [Types.INITIAL]: {
    user: AuthUserType;
  };
  [Types.LOGIN]: {
    user: AuthUserType;
  };
  [Types.REGISTER]: {
    user: AuthUserType;
  };
  [Types.LOGOUT]: undefined;
};

type ActionsType = ActionMapType<Payload>[keyof ActionMapType<Payload>];

// ----------------------------------------------------------------------

const initialState: AuthStateType = {
  user: null,
  loading: true,
};

const reducer = (state: AuthStateType, action: ActionsType) => {
  if (action.type === Types.INITIAL) {
    return {
      loading: false,
      user: action.payload.user,
    };
  }
  if (action.type === Types.LOGIN) {
    return {
      ...state,
      user: action.payload.user,
    };
  }
  if (action.type === Types.REGISTER) {
    return {
      ...state,
      user: action.payload.user,
    };
  }
  if (action.type === Types.LOGOUT) {
    return {
      ...state,
      user: null,
    };
  }
  return state;
};

// ----------------------------------------------------------------------

const STORAGE_KEY = 'lbankaAccessToken';

type Props = {
  children: React.ReactNode;
};

export function AuthProvider({ children }: Props) {
  const [state, dispatch] = useReducer(reducer, initialState);

  const initialize = useCallback(async () => {
    try {
      const accessToken = localStorage.getItem(STORAGE_KEY);
      // Get the JWT token from the cookie
      // const accessToken = Cookies.get('lbankaAccessToken');

      if (accessToken && isValidToken(accessToken)) {

        setSession(accessToken);

        const response = await axios.get(`${HOST_API}/auth/me`);

        const { user } = response.data;

        dispatch({
          type: Types.INITIAL,
          payload: {
            user,
          },
        });
      } else {
        dispatch({
          type: Types.INITIAL,
          payload: {
            user: null,
          },
        });
      }
    } catch (error) {
      console.error(error);
      dispatch({
        type: Types.INITIAL,
        payload: {
          user: null,
        },
      });
    }
  }, []);

  useEffect(() => {
    initialize();
  }, [initialize]);

  // LOGIN
  const login = useCallback(
    async (email: string, password: string) => {
      try {
        const data: Record<string, any> = {
          email,
          password,
        };
        // Extract 'invitation' from searchParams if it exists
        const invitation = localStorage.getItem('invitation_token');
        if (invitation) {
          data.invitation = invitation;
        }
        // Extract 'ref' from searchParams if it exists
        const ref = localStorage.getItem('ref_token');
        if (ref) {
          data.ref = ref;
        }
        
        const response = await axios.post(`${HOST_API}/auth/login`, data, { withCredentials: true });

        const { accessToken, user } = response.data;
        setSession(accessToken);
        dispatch({
          type: Types.LOGIN,
          payload: {
            user,
          },
        });

      } catch (error: any) {
        console.log(error);
        if (error.messages) {
          throw error.messages;
        }
        throw new Error('Login failed. Please try again.');
      }
    },
    []
  );

  // REGISTER
  const register = useCallback(
    async (email: string, password: string, firstName: string, lastName: string) => {
      try {
        // Prepare base registration data
        const data: Record<string, any> = {
          email,
          password,
          firstName,
          lastName,
        };
  
        // Extract 'invitation' from searchParams if it exists
        const invitation = localStorage.getItem('invitation_token');
        if (invitation) {
          data.invitation = invitation;
        }
        // Extract 'ref' from searchParams if it exists
        const ref = localStorage.getItem('ref_token');
        if (ref) {
          data.ref = ref;
        }
        // Make the API call to register the user
        const response = await axios.post(`${HOST_API}/auth/register`, data, { withCredentials: true });
  
        const { accessToken, user } = response.data;
  
        // Save the token in localStorage or setSession as required
        setSession(accessToken);
  
        // Dispatch the user data to the context
        dispatch({
          type: Types.REGISTER,
          payload: {
            user,
          },
        });
  
      } catch (error: any) {
        console.log(error);
        
        // Check if the error has a response (e.g., validation errors from the server)
        if (error.response && error.response.data && error.response.data.messages) {
          // Throw the error response to be handled by the caller
          throw error.response.data.messages;
        }
  
        // If it's a general error (e.g., network or unknown error)
        throw new Error(error.message || 'Registration failed. Please try again.');
      }
    },
    []
  );

  // LOGOUT
  const logout = useCallback(async () => {
    setSession(null);
    dispatch({
      type: Types.LOGOUT,
    });
  }, []);

  // ----------------------------------------------------------------------

  const checkAuthenticated = state.user ? 'authenticated' : 'unauthenticated';

  const status = state.loading ? 'loading' : checkAuthenticated;

  const memoizedValue = useMemo(
    () => ({
      user: state.user,
      method: 'jwt',
      loading: status === 'loading',
      authenticated: status === 'authenticated',
      unauthenticated: status === 'unauthenticated',
      //
      login,
      register,
      logout,
    }),
    [login, logout, register, state.user, status]
  );

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}
